// src/antdviews/ResetView.tsx

// 重置密码页面 - Ant Design 重构版

import React from 'react'
import localStorage from 'localStorage'
import { withTranslation, WithTranslation } from 'react-i18next'
import axios from 'axios'
import {
    Form,
    Input,
    Button,
    Select,
    Modal,
    Row,
    Col,
    Card,
    Typography,
    Space,
    Alert,
} from 'antd'
import {
    UserOutlined,
    LockOutlined,
    GlobalOutlined,
    MailOutlined,
    SafetyOutlined,
    ArrowLeftOutlined,
    SendOutlined,
} from '@ant-design/icons'
import { AuthApi, ROOT } from '../services/Api'
import { Global } from '../services/Global'
import { browserUtil } from '../utils/BrowserUtil'
import { encrypUtil } from '../utils/EncrypUtil'
import { Page } from '../services/Page'

const { Title, Text } = Typography
const { Option } = Select

interface ResetViewState {
    username: string
    password: string
    repeatPassword: string
    verificationCode: string
    lang: string
    warnTitle: string
    warnInfo: string
    successTitle: string
    successInfo: string
    rememberMe: boolean
    showModal: boolean
    showSuccessModal: boolean
    loading: boolean
    sendingPin: boolean
}

class ResetView extends React.Component<WithTranslation, ResetViewState> {

    constructor(props: WithTranslation) {
        super(props)
        this.state = {
            username: localStorage.getItem('username') || '',
            password: '',
            repeatPassword: '',
            verificationCode: '',
            lang: 'zh_CN',
            warnTitle: '',
            warnInfo: '',
            successTitle: '',
            successInfo: '',
            rememberMe: localStorage.getItem('rememberMe') === '1',
            showModal: false,
            showSuccessModal: false,
            loading: false,
            sendingPin: false,
        }
    }

    componentWillMount() {
        Global.isMobile = browserUtil.checkMobile()
    }

    componentDidMount() {
        const DEBUG = false
        const TAG = `ResetView.componentDidMount() - `
        const rememberMe = localStorage.getItem('rememberMe')
        DEBUG && console.log(TAG, `rememberMe:`, rememberMe)
        this.setState({ rememberMe: rememberMe === '1' })

        const lang = localStorage.getItem('lang') || 'zh_CN'
        this.changeLanguage(lang)
    }

    changeLanguage = (lang: string) => {
        const { i18n } = this.props
        i18n.changeLanguage(lang)
        this.setState({ lang })
    }

    openModal = () => {
        this.setState({ showModal: true })
    }

    closeModal = () => {
        this.setState({ showModal: false })
    }

    openSuccessModal = () => {
        this.setState({ showSuccessModal: true })
    }

    closeSuccessModal = () => {
        this.setState({ showSuccessModal: false })
    }

    sendPin = async () => {
        const DEBUG = false
        const TAG = `ResetView.sendPin() - `
        DEBUG && console.log(TAG, `发送校验码`)
        const { t } = this.props

        if (!this.state.username) {
            this.setState({
                warnTitle: t('localError'),
                warnInfo: t('emptyAccount'),
            })
            this.openModal()
            return
        }

        this.setState({ sendingPin: true })
        try {
            const requestData = {
                email: this.state.username,
                lang: this.state.lang.split('_')[0],
            }
            const params = await encrypUtil.encodeRequestRsa(requestData)
            const response = await axios.post(AuthApi.SEND_PIN, params)
            DEBUG && console.log(TAG, `response.data:`, response.data)
            if (400 === response.data.code) {
                this.setState({
                    warnTitle: t('serverError'),
                    warnInfo: response.data.message,
                })
                this.openModal()
                return
            }
            if (200 === response.data.code) {
                this.setState({
                    successTitle: t('success'),
                    successInfo: t('pinSent'),
                })
                this.openSuccessModal()
            }
        } finally {
            this.setState({ sendingPin: false })
        }
    }

    handleReset = async () => {
        const DEBUG = true
        const TAG = `ResetView.render().handleReset() - `
        DEBUG && console.log(TAG, '重置密码')
        const { t } = this.props

        // 表单验证
        if (!this.state.username) {
            this.setState({
                warnTitle: t('localError'),
                warnInfo: t('emptyAccount'),
            })
            this.openModal()
            return
        }
        if (!this.state.password) {
            this.setState({
                warnTitle: t('localError'),
                warnInfo: t('emptyPassword'),
            })
            this.openModal()
            return
        }
        if (this.state.password !== this.state.repeatPassword) {
            this.setState({
                warnTitle: t('localError'),
                warnInfo: t('passwordMismatch'),
            })
            this.openModal()
            return
        }
        if (!this.state.verificationCode) {
            this.setState({
                warnTitle: t('localError'),
                warnInfo: t('emptyVerificationCode'),
            })
            this.openModal()
            return
        }

        this.setState({ loading: true })
        try {
            const requestData = {
                email: this.state.username,
                pin: this.state.verificationCode,
                password: this.state.password,
                lang: this.state.lang.split('_')[0],
            }
            const params = await encrypUtil.encodeRequestRsa(requestData)
            const response = await axios.post(AuthApi.RESET_PWD, params)
            DEBUG && console.log(TAG, `response.data:`, response.data)
            if (400 === response.data.code) {
                this.setState({
                    warnTitle: t('serverError'),
                    warnInfo: response.data.message,
                })
                this.openModal()
                return
            }
            if (200 === response.data.code) {
                this.setState({
                    successTitle: t('success'),
                    successInfo: t('resetSuccess'),
                })
                this.openSuccessModal()
            }
        } finally {
            this.setState({ loading: false })
        }
    }

    gotoLogin = () => {
        const DEBUG = false
        const TAG = `ResetView.render().gotoLogin() - `
        DEBUG && console.log(TAG, '跳转到登录页面')
        window.location.href = `${ROOT}/${Page.login}`
    }

    handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ username: e.target.value })
    }

    handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ password: e.target.value })
    }

    handleRepeatPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ repeatPassword: e.target.value })
    }

    handleVerificationCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ verificationCode: e.target.value })
    }

    render() {
        const { t } = this.props
        const lang = localStorage.getItem('lang') || 'zh_CN'
        const selectedLang = lang === 'en_US' ? 'en_US' : 'zh_CN'

        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f0f2f5',
            }}>
                {/* 警告弹窗 */}
                <Modal
                    title={
                        <span>
                            <span style={{ color: '#faad14', marginRight: 8 }}>⚠</span>
                            {t('error')}
                        </span>
                    }
                    open={this.state.showModal}
                    onCancel={this.closeModal}
                    footer={[
                        <Button key="confirm" type="primary" onClick={this.closeModal}>
                            {t('confirm')}
                        </Button>,
                    ]}
                    centered
                >
                    <Text type="danger">{this.state.warnInfo}</Text>
                </Modal>

                {/* 成功弹窗 */}
                <Modal
                    title={
                        <span>
                            <span style={{ color: '#52c41a', marginRight: 8 }}>✓</span>
                            {t('success')}
                        </span>
                    }
                    open={this.state.showSuccessModal}
                    onCancel={this.closeSuccessModal}
                    footer={[
                        <Button
                            key="gotoLogin"
                            type="primary"
                            onClick={this.gotoLogin}
                        >
                            {t('gotoLogin')}
                        </Button>,
                        <Button key="confirm" onClick={this.closeSuccessModal}>
                            {t('confirm')}
                        </Button>,
                    ]}
                    centered
                >
                    <Text>{this.state.successInfo}</Text>
                </Modal>

                <Card
                    style={{
                        width: 420,
                        maxWidth: '96%',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        borderRadius: 8,
                    }}
                    bodyStyle={{ padding: '32px 40px' }}
                >
                    {/* 顶部：标题 + 语言切换 */}
                    <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
                        <Col>
                            <Space>
                                <Title level={3} style={{ margin: 0 }}>
                                    <LockOutlined style={{ marginRight: 8 }} />
                                    {t('resetPwd')}
                                </Title>
                            </Space>
                        </Col>
                        <Col>
                            <Select
                                value={selectedLang}
                                onChange={(value: string) => {
                                    localStorage.setItem('lang', value)
                                    this.setState({ lang: value })
                                    this.changeLanguage(value)
                                }}
                                style={{ width: 120 }}
                                suffixIcon={<GlobalOutlined />}
                            >
                                <Option value="zh_CN">简体中文</Option>
                                <Option value="en_US">English</Option>
                            </Select>
                        </Col>
                    </Row>

                    <Form layout="vertical" onFinish={this.handleReset}>
                        {/* 邮箱 + 发送验证码按钮 */}
                        <Form.Item
                            label={t('accountEmail')}
                            required={false}
                            rules={[{ required: true, message: t('emptyAccount') }]}
                        >
                            <Space.Compact style={{ width: '100%' }}>
                                <Input
                                    prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                                    placeholder={t('accountEmail')}
                                    value={this.state.username}
                                    onChange={this.handleUsernameChange}
                                    size="large"
                                    style={{ flex: 1 }}
                                />
                                <Button
                                    type="primary"
                                    size="large"
                                    loading={this.state.sendingPin}
                                    onClick={this.sendPin}
                                    icon={<SendOutlined />}
                                >
                                    {t('sendPin')}
                                </Button>
                            </Space.Compact>
                        </Form.Item>

                        {/* 新密码 */}
                        <Form.Item
                            label={t('password')}
                            required={false}
                            rules={[{ required: true, message: t('emptyPassword') }]}
                        >
                            <Input.Password
                                prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                                placeholder={t('password')}
                                value={this.state.password}
                                onChange={this.handlePasswordChange}
                                size="large"
                            />
                        </Form.Item>

                        {/* 重复密码 */}
                        <Form.Item
                            label={t('repeatPassword')}
                            required={false}
                            rules={[{ required: true, message: t('emptyRepeatPassword') }]}
                        >
                            <Input.Password
                                prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                                placeholder={t('repeatPassword')}
                                value={this.state.repeatPassword}
                                onChange={this.handleRepeatPasswordChange}
                                size="large"
                            />
                        </Form.Item>

                        {/* 验证码 */}
                        <Form.Item
                            label={t('verificationCode')}
                            required={false}
                            rules={[{ required: true, message: t('emptyVerificationCode') }]}
                        >
                            <Input
                                prefix={<SafetyOutlined style={{ color: '#bfbfbf' }} />}
                                placeholder={t('verificationCode')}
                                value={this.state.verificationCode}
                                onChange={this.handleVerificationCodeChange}
                                size="large"
                            />
                        </Form.Item>

                        {/* 操作按钮 */}
                        <Space direction="vertical" size={12} style={{ width: '100%', marginTop: 8 }}>
                            <Button
                                type="primary"
                                block
                                size="large"
                                loading={this.state.loading}
                                onClick={this.handleReset}
                                icon={<LockOutlined />}
                            >
                                {t('resetPwd')}
                            </Button>
                            <Button
                                block
                                size="large"
                                onClick={this.gotoLogin}
                                icon={<ArrowLeftOutlined />}
                            >
                                {t('gotoLogin')}
                            </Button>
                        </Space>
                    </Form>
                </Card>
            </div>
        )
    }
}

export const ResetPage = withTranslation()(ResetView)